import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import {
  handleLoginCallback,
  loginSuccess,
  loginFailure,
  logout,
  completeCallback,
  completeCallbackSuccess,
  completeCallbackFailure,
  logoutSuccess,
  logoutFailure,
} from './auth.actions';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import { loadUserProfileFailure, loadUserProfileSuccess } from '@/store/current-user/profile.actions';
import { TokenMonitorService } from '../token-monitor.service';
import { CurrentUserStore } from '@/store/current-user/current-user.store';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService: AuthService = inject(AuthService);
  private router = inject(Router);
  private tokenMonitorService = inject(TokenMonitorService);
  private currentUserStore = inject(CurrentUserStore);

  handleLoginCallback$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(handleLoginCallback),
      mergeMap(() =>
        this.authService.handleLoginCallback().pipe(
          mergeMap((authResult) => {
            console.log('Auth Result:', authResult);
            if (authResult.isAuthenticated) {
              return [completeCallback({ token: authResult.idToken })];
            } else {
              return of(loginFailure({ error: 'Authentication failed' }));
            }
          }),
          catchError((error) => of(loginFailure({ error })))
        )
      )
    );
  });

  completeCallback$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(completeCallback),
      switchMap((action) =>
        this.authService.completeCallback(action.token).pipe(
          switchMap(async (profile) => {
            console.log('🔄 [AuthEffects] Setting user profile in CurrentUserStore');
            // Set user in CurrentUserStore
            this.currentUserStore.setUser(profile);

            console.log('🔄 [AuthEffects] Loading user teams...');
            // Load user teams to ensure CurrentUserStore.isReady becomes true
            await this.currentUserStore.loadUserTeams();
            console.log('✅ [AuthEffects] User teams loaded, store is ready');

            // Load the full profile (with roles) into the ngrx store BEFORE the
            // post-login navigation: the role guards on the stored redirect URL
            // (e.g. /admin/...) read it there. Best-effort — a failure settles
            // the load so the guards deny instead of waiting.
            const profileAction = await firstValueFrom(
              this.authService.getUserProfile(action.token),
            ).then(
              (userProfile) => loadUserProfileSuccess({ userProfile }),
              (error) => loadUserProfileFailure({ error }),
            );

            // Return the profile so we can dispatch actions
            return { profile, profileAction };
          }),
          mergeMap(({ profile, profileAction }) => [
            profileAction,
            completeCallbackSuccess({ profile }),
            loginSuccess({ userData: profile })
          ]),
          catchError((error) => {
            console.error('❌ [AuthEffects] Error in completeCallback:', error);
            return of(loadUserProfileFailure({ error }), completeCallbackFailure({ error }));
          })
        )
      )
    );
  });

  navigateAfterLogin$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(completeCallbackSuccess),
        tap(() => {
          console.log('🎯 [AuthEffects] completeCallbackSuccess received, preparing to navigate');

          // Start token monitoring after successful login
          this.tokenMonitorService.startMonitoring();

          // Check CurrentUserStore state before navigation
          const isReady = this.currentUserStore.isReady();
          const user = this.currentUserStore.user();
          const teams = this.currentUserStore.teams();
          console.log('🔍 [AuthEffects] CurrentUserStore state:', {
            isReady,
            hasUser: !!user,
            teamsCount: teams.length,
            userId: user?.id
          });

          // Check for stored redirect URL
          const storedUrl = sessionStorage.getItem('redirectUrl');
          console.log('📌 [AuthEffects] Stored redirect URL from sessionStorage:', storedUrl);
          const redirectUrl = storedUrl || '/apps/opportunity-management/in-progress';

          // Clear the stored URL
          sessionStorage.removeItem('redirectUrl');

          console.log('🚀 [AuthEffects] Attempting navigation to:', redirectUrl);
          this.router.navigateByUrl(redirectUrl).then(
            (success) => console.log('✅ [AuthEffects] Navigation completed:', success),
            (error) => console.error('❌ [AuthEffects] Navigation failed:', error)
          );
        })
      );
    },
    { dispatch: false }
  );

  // Note: This effect is kept for backwards compatibility but is no longer the primary logout flow.
  // The Logout component now handles the full logout process directly including:
  // 1. Stopping token monitoring
  // 2. Clearing CurrentUserStore
  // 3. Dispatching logout action
  // 4. Calling OIDC logoff (which redirects to Cognito)
  // This effect may still be triggered by the logout action dispatch, but the actual
  // logout has already been initiated by the component.
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      tap(() => {
        console.log('🚪 [AuthEffects] Logout action received');
        // Stop token monitoring (defensive - component should have already done this)
        this.tokenMonitorService.stopMonitoring();
        // Clear user store (defensive - component should have already done this)
        this.currentUserStore.clearUser();
      }),
      map(() => logoutSuccess()),
      catchError((error) => {
        console.error('🚪 [AuthEffects] Logout error:', error);
        return of(logoutFailure(error));
      })
    )
  );

  // Note: No navigation on logoutSuccess because OIDC library handles the redirect to Cognito
  // which then redirects to postLogoutRedirectUri (configured in environment files)
}
